import React, { useState, useEffect, useRef, useCallback } from 'react';

const ScrollableTabs = ({ tabs, activeTab, onTabChange }) => {
  const [canScroll, setCanScroll] = useState({ left: false, right: true });
  const tabsContainerRef = useRef(null);

  const checkScroll = useCallback(() => {
    if (!tabsContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = tabsContainerRef.current;
    const hasHorizontalScroll = scrollWidth > clientWidth;

    setCanScroll({
      left: hasHorizontalScroll && scrollLeft > 0,
      right: hasHorizontalScroll && scrollLeft < scrollWidth - clientWidth - 1
    });
  }, []);

  const scrollTabs = useCallback((direction) => {
    if (!tabsContainerRef.current) return;

    const container = tabsContainerRef.current;
    const scrollAmount = 200;
    const currentScroll = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    const newScroll = direction === 'left'
      ? Math.max(0, currentScroll - scrollAmount)
      : Math.min(maxScroll, currentScroll + scrollAmount);

    container.scrollTo({ left: newScroll, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    // Initial check
    checkScroll();

    // Add event listeners
    container.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      resizeObserver.disconnect();
    };
  }, [checkScroll]);

  return (
    <div className="tabs-container">
      <button
        className={`scroll-arrow left-arrow ${!canScroll.left ? 'disabled' : ''}`}
        onClick={() => scrollTabs('left')}
        aria-label="Scroll left"
        disabled={!canScroll.left}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 15 13" fill="none">
          <path
            d="M0.904785 6.33397L13.573 6.33397M0.904785 6.33397L6.334 11.7632M0.904785 6.33397L6.334 0.90475"
            stroke={!canScroll.left ? '#CCC' : 'black'}
            strokeWidth="1.80974"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="tab-buttons" ref={tabsContainerRef}>
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            aria-current={activeTab === id ? 'page' : undefined}
            className={`tab-button ${activeTab === id ? 'active' : ''}`}
            onClick={() => onTabChange(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <button
        className={`scroll-arrow right-arrow ${!canScroll.right ? 'disabled' : ''}`}
        onClick={() => scrollTabs('right')}
        aria-label="Scroll right"
        disabled={!canScroll.right}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 15 13" fill="none">
          <path
            d="M13.573 6.33397L0.904826 6.33397M13.573 6.33397L8.14378 11.7632M13.573 6.33397L8.14378 0.90475"
            stroke={!canScroll.right ? '#CCC' : 'black'}
            strokeWidth="1.80974"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default ScrollableTabs;